import { ChevronsLeft, ChevronsRight } from "lucide-react";

import { NavItem } from "@/app/layouts/dashboard/NavItem";
import { useSidebar } from "@/app/layouts/dashboard/sidebar-context-core";
import { BrandMark } from "@/features/auth/presentation/components/BrandMark";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { NAV_ITEMS, type NavItem as NavItemConfig } from "@/shared/config/navigation";
import { ENV } from "@/shared/lib/env";
import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/store";
import { selectHasAnyPermission } from "@/store/selectors";

/**
 * Sidebar persistente en desktop (lg+). Dos modos:
 *   - Expandido: 256px, muestra iconos + labels.
 *   - Colapsado: 72px, solo iconos con tooltips.
 *
 * El toggle vive al fondo. El estado colapsado se persiste en
 * localStorage (`sidebar-context`).
 */
interface AppSidebarProps {
  /** Si está en una Sheet móvil, el toggle colapsar/expandir no aplica. */
  variant?: "desktop" | "mobile";
}

function GuardedNavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItemConfig;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const hasAny = useAppSelector(selectHasAnyPermission(item.allow ?? []));
  if (item.allow && !hasAny) return null;
  return (
    <NavItem
      to={item.to}
      label={item.label}
      icon={item.icon}
      collapsed={collapsed}
      onNavigate={onNavigate}
    />
  );
}

export function AppSidebar({ variant = "desktop" }: AppSidebarProps) {
  const { collapsed, toggleCollapsed, closeMobile } = useSidebar();

  const isCollapsed = variant === "desktop" && collapsed;
  const onNavigate = variant === "mobile" ? closeMobile : undefined;
  const envLabel =
    ENV.VITE_ENVIRONMENT === "production"
      ? "Producción"
      : ENV.VITE_ENVIRONMENT === "staging"
        ? "Staging"
        : "Desarrollo";
  const isProd = ENV.VITE_ENVIRONMENT === "production";

  return (
    <div
      className={cn(
        "flex h-full flex-col bg-card",
        variant === "desktop" && "border-r border-border/60",
      )}
    >
      {/* Brand header */}
      <div
        className={cn(
          "flex h-16 items-center border-b border-border/60 px-5 transition-all duration-300",
          isCollapsed && "justify-center px-2",
        )}
      >
        {isCollapsed ? (
          <div
            aria-label="Arya"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15"
          >
            <span className="font-display text-base font-medium tracking-display-tight">A</span>
          </div>
        ) : (
          <BrandMark tone="onLight" size="md" />
        )}
      </div>

      {/* Env chip — solo en non-prod y expandido */}
      {!isProd && !isCollapsed ? (
        <div className="px-5 pt-4">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1",
              "border-amber-300/50 bg-amber-50/80",
              "font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-amber-900",
            )}
          >
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            {envLabel}
          </span>
        </div>
      ) : null}

      {/* Nav */}
      <nav
        aria-label="Navegación principal"
        className={cn(
          "flex-1 overflow-y-auto px-3 pb-3 pt-5 transition-all",
          isCollapsed && "px-2",
        )}
      >
        {!isCollapsed ? (
          <div className="mb-3 px-3 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Navegación
          </div>
        ) : null}
        <ul className="space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <GuardedNavItem item={item} collapsed={isCollapsed} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer: collapse toggle (solo desktop) + version */}
      {variant === "desktop" ? (
        <div className={cn("border-t border-border/60 p-3", isCollapsed && "px-2")}>
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleCollapsed}
                aria-label={isCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
                aria-pressed={isCollapsed}
                className={cn(
                  "w-full justify-start text-muted-foreground hover:text-foreground",
                  isCollapsed && "justify-center px-0",
                )}
              >
                {isCollapsed ? (
                  <ChevronsRight className="h-4 w-4" />
                ) : (
                  <>
                    <ChevronsLeft className="h-4 w-4" />
                    <span className="text-[12px]">Colapsar</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            {isCollapsed ? (
              <TooltipContent side="right" sideOffset={12}>
                Expandir
              </TooltipContent>
            ) : null}
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}
